/*
 * Device-Setup-Modal: Verbindung + (optional) Zugangsdaten für ein
 * klassifiziertes Device. Zwei Sektionen im gleichen Modal, weil sie
 * zusammen einen kompletten Setup-Schritt sind — der Techniker klickt
 * einmal auf "Test" und einmal auf "Speichern".
 *
 * Server-side liegen die Daten in zwei getrennten Tabellen
 * (device_connections plain, device_credentials AES-GCM). Das UI kennt
 * den Unterschied nur an der Sektionsüberschrift und daran, dass
 * secret-Felder mit *** maskiert zurückkommen und beim Fokussieren
 * geleert werden.
 *
 * BACnet-IP / Modbus-TCP haben KEIN Cred-Schema — das Modal blendet die
 * Zugangsdaten-Sektion dann komplett aus.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { ApplySchemaTemplateModal, type ApplyResult } from './ApplySchemaTemplateModal';
import { HeroCredentials, CredentialsTest, IconCancel, IconSave, IconTest, IconTemplateApply } from '../icons/HvacIcons';
import './hv-modal.css';

interface CredentialEnumMember {
  value: string;
  label: string;
}

interface CredentialProperty {
  key: string;
  title: string;
  type: 'integer' | 'string' | 'secret' | 'boolean';
  required: boolean;
  default: unknown;
  placeholder: string | null;
  helpText: string | null;
  format: 'url' | 'host' | 'port' | 'enum' | null;
  enumMembers: CredentialEnumMember[] | null;
}

interface Schema {
  protocol: string;
  properties: CredentialProperty[];
}

interface ValuesMap {
  [key: string]: unknown;
}

interface GetConnectionResponse {
  deviceId: string;
  protocol: string;
  updatedAt: string;
  values: ValuesMap;
}

interface GetCredentialsResponse {
  deviceId: string;
  protocol: string;
  keyVersion: number;
  updatedAt: string;
  values: ValuesMap;
}

interface TestCredentialsResponse {
  success: boolean;
  error: string | null;
}

interface Props {
  deviceId: string;
  protocol: string;
  deviceDisplayName: string;
  /** Scan-IP als Vorbelegung für host-/url-Felder. */
  hostHint: string;
  /** Wenn gesetzt, wird nach erfolgreichem Test der Button "Template
   *  anwenden…" freigeschaltet. Ohne plantId geht das Apply-Endpoint
   *  nicht — die Feature wird dann komplett unterdrückt. */
  plantId?: string;
  onSaved(): void;
  onClose(): void;
  /** Optionaler Hook: der Aufrufer weiß, ob der Ziel-Baum leer ist (z.B.
   *  weil er die Plant-Schema-Antwort schon vorliegen hat). Bei
   *  `undefined` gehen wir defensiv von `false` aus — Overwrite-Row wird
   *  gezeigt, muss aber explizit bestätigt werden. */
  isTargetEmpty?: boolean;
  /** Callback nach erfolgreichem Template-Apply. Verpflichtender Refresh-
   *  Hook für den Parent, sonst zeigt der Baum das alte Schema. */
  onSchemaApplied?(result: ApplyResult): void;
}

const SECRET_MASK = '***';

type TestStatus =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'passed' }
  | { kind: 'failed'; error: string };

interface UrlSegments {
  scheme: 'http' | 'https';
  host: string;
  port: string;
  manualOverride: boolean;
  manualValue: string;
}

function parseUrl(raw: string, hostHint: string): UrlSegments {
  if (!raw) {
    return { scheme: 'https', host: hostHint, port: '', manualOverride: false, manualValue: '' };
  }
  try {
    const u = new URL(raw);
    if (u.protocol === 'http:' || u.protocol === 'https:') {
      return {
        scheme: u.protocol === 'https:' ? 'https' : 'http',
        host: u.hostname,
        port: u.port,
        manualOverride: false,
        manualValue: raw,
      };
    }
  } catch { /* fall through */ }
  return { scheme: 'https', host: hostHint, port: '', manualOverride: true, manualValue: raw };
}

function composeUrl(seg: UrlSegments): string {
  if (seg.manualOverride) return seg.manualValue;
  if (!seg.host) return '';
  const portPart = seg.port ? `:${seg.port}` : '';
  return `${seg.scheme}://${seg.host}${portPart}`;
}

export function CredentialsModal({
  deviceId,
  protocol,
  deviceDisplayName,
  hostHint,
  plantId,
  onSaved,
  onClose,
  isTargetEmpty,
  onSchemaApplied,
}: Props) {
  const [connectionValues, setConnectionValues] = useState<ValuesMap>({});
  const [credentialValues, setCredentialValues] = useState<ValuesMap>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialised, setInitialised] = useState(false);
  const [testStatus, setTestStatus] = useState<TestStatus>({ kind: 'idle' });
  const [urlSegments, setUrlSegments] = useState<Record<string, UrlSegments>>({});
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);

  // Connection-Schema ist immer da; Credentials-Schema ist optional
  // (404 wenn Protokoll keine Auth hat).
  const connectionSchemaState = useAsync(
    (signal) => api.get<Schema>(
      `/api/devices/${deviceId}/connection/schema?protocol=${encodeURIComponent(protocol)}`,
      signal),
    [deviceId, protocol],
  );
  const credentialsSchemaState = useAsync(
    (signal) => api.get<Schema>(
      `/api/devices/${deviceId}/credentials/schema?protocol=${encodeURIComponent(protocol)}`,
      signal).catch((e) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }),
    [deviceId, protocol],
  );
  const existingConnectionState = useAsync(
    (signal) => api.get<GetConnectionResponse>(`/api/devices/${deviceId}/connection`, signal)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }),
    [deviceId],
  );
  const existingCredentialsState = useAsync(
    (signal) => api.get<GetCredentialsResponse>(`/api/devices/${deviceId}/credentials`, signal)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }),
    [deviceId],
  );

  const hasCredentials =
    credentialsSchemaState.status === 'ok' && credentialsSchemaState.data !== null;

  const isUrlField = (p: CredentialProperty) => p.format === 'url';
  const isHostField = (p: CredentialProperty) => p.format === 'host';
  const isPortField = (p: CredentialProperty) => p.format === 'port';
  const isEnumField = (p: CredentialProperty) => p.format === 'enum';

  // Init form once all four async states resolved.
  useEffect(() => {
    if (initialised) return;
    if (connectionSchemaState.status !== 'ok') return;
    if (credentialsSchemaState.status !== 'ok') return;
    if (existingConnectionState.status === 'loading') return;
    if (existingCredentialsState.status === 'loading') return;

    const initConn: ValuesMap = {};
    const segs: Record<string, UrlSegments> = {};
    for (const p of connectionSchemaState.data.properties) {
      initConn[p.key] = p.default ?? (p.type === 'boolean' ? false : '');
      if (isHostField(p) && (!initConn[p.key] || initConn[p.key] === '')) {
        initConn[p.key] = hostHint;
      }
    }
    if (existingConnectionState.status === 'ok' && existingConnectionState.data) {
      for (const [k, v] of Object.entries(existingConnectionState.data.values)) {
        initConn[k] = v ?? initConn[k];
      }
    }
    for (const p of connectionSchemaState.data.properties) {
      if (isUrlField(p)) {
        const raw = typeof initConn[p.key] === 'string' ? (initConn[p.key] as string) : '';
        segs[p.key] = parseUrl(raw, hostHint);
        initConn[p.key] = composeUrl(segs[p.key]);
      }
    }

    const initCred: ValuesMap = {};
    if (credentialsSchemaState.data) {
      for (const p of credentialsSchemaState.data.properties) {
        initCred[p.key] = p.default ?? (p.type === 'boolean' ? false : '');
      }
      if (existingCredentialsState.status === 'ok' && existingCredentialsState.data) {
        for (const [k, v] of Object.entries(existingCredentialsState.data.values)) {
          initCred[k] = v ?? initCred[k];
        }
      }
    }

    setConnectionValues(initConn);
    setCredentialValues(initCred);
    setUrlSegments(segs);
    setInitialised(true);
  }, [
    connectionSchemaState, credentialsSchemaState,
    existingConnectionState, existingCredentialsState,
    initialised, hostHint,
  ]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, saving]);

  const invalidateTest = () => {
    setTestStatus((cur) => cur.kind === 'passed' || cur.kind === 'failed' ? { kind: 'idle' } : cur);
  };
  const setConnField = (key: string, value: unknown) => {
    setConnectionValues((cur) => ({ ...cur, [key]: value }));
    setTouched((cur) => {
      const k = `conn:${key}`;
      if (cur.has(k)) return cur;
      const next = new Set(cur); next.add(k); return next;
    });
    invalidateTest();
  };
  const setCredField = (key: string, value: unknown) => {
    setCredentialValues((cur) => ({ ...cur, [key]: value }));
    setTouched((cur) => {
      const k = `cred:${key}`;
      if (cur.has(k)) return cur;
      const next = new Set(cur); next.add(k); return next;
    });
    invalidateTest();
  };

  const setUrlSegment = (key: string, patch: Partial<UrlSegments>) => {
    setUrlSegments((cur) => {
      const before = cur[key] ?? { scheme: 'https', host: hostHint, port: '', manualOverride: false, manualValue: '' };
      const next = { ...before, ...patch };
      const composed = composeUrl(next);
      setConnField(key, composed);
      return { ...cur, [key]: next };
    });
  };

  /** Normalise both sections into the payload the server expects. */
  const buildPayload = (): { connection: ValuesMap; credentials: ValuesMap } | Error => {
    if (connectionSchemaState.status !== 'ok') return new Error('Verbindungs-Schema nicht geladen.');
    const connection: ValuesMap = {};
    for (const p of connectionSchemaState.data.properties) {
      let v = connectionValues[p.key];
      if (p.type === 'integer') {
        if (v === '' || v === null || v === undefined) v = null;
        else {
          const n = Number(v);
          if (Number.isNaN(n)) return new Error(`${p.title}: Zahl erwartet.`);
          v = n;
        }
      }
      if (p.type === 'boolean') v = Boolean(v);
      if ((p.type === 'string' || p.type === 'secret') && v !== null && v !== undefined) {
        v = String(v);
        if (v === '' && !p.required) v = null;
      }
      connection[p.key] = v;
    }
    const credentials: ValuesMap = {};
    if (hasCredentials && credentialsSchemaState.status === 'ok' && credentialsSchemaState.data) {
      for (const p of credentialsSchemaState.data.properties) {
        let v = credentialValues[p.key];
        // Untouched secret stays as SECRET_MASK — server carries forward.
        if (p.type === 'secret' && !touched.has(`cred:${p.key}`) && v === SECRET_MASK) {
          v = SECRET_MASK;
        }
        if (p.type === 'integer') {
          if (v === '' || v === null || v === undefined) v = null;
          else {
            const n = Number(v);
            if (Number.isNaN(n)) return new Error(`${p.title}: Zahl erwartet.`);
            v = n;
          }
        }
        if (p.type === 'boolean') v = Boolean(v);
        if ((p.type === 'string' || p.type === 'secret') && v !== null && v !== undefined) {
          v = String(v);
          if (v === '' && !p.required) v = null;
        }
        credentials[p.key] = v;
      }
    }
    return { connection, credentials };
  };

  const runTest = async () => {
    const payload = buildPayload();
    if (payload instanceof Error) {
      setTestStatus({ kind: 'failed', error: payload.message });
      return;
    }
    setTestStatus({ kind: 'running' });
    setError(null);
    try {
      const res = await api.post<TestCredentialsResponse>(
        `/api/devices/${deviceId}/credentials/test`,
        { protocol, connection: payload.connection, credentials: payload.credentials });
      if (res.success) {
        setTestStatus({ kind: 'passed' });
      } else {
        setTestStatus({ kind: 'failed', error: res.error ?? 'Test fehlgeschlagen (kein Fehlerdetail).' });
      }
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string; detail?: string })?.error
          ?? (e.body as { detail?: string })?.detail;
        setTestStatus({ kind: 'failed', error: bodyMsg ?? `Test-Endpoint HTTP ${e.status}` });
      } else if (e instanceof Error) {
        setTestStatus({ kind: 'failed', error: e.message });
      } else {
        setTestStatus({ kind: 'failed', error: 'Unbekannter Fehler.' });
      }
    }
  };

  const save = async () => {
    const payload = buildPayload();
    if (payload instanceof Error) {
      setError(payload.message);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Two PUTs — connection first (mandatory), credentials only when
      // the protocol has an auth schema. Sequential so a failure on the
      // connection side surfaces cleanly before we touch secrets.
      await api.put<GetConnectionResponse>(
        `/api/devices/${deviceId}/connection`,
        { protocol, values: payload.connection });
      if (hasCredentials) {
        await api.put<GetCredentialsResponse>(
          `/api/devices/${deviceId}/credentials`,
          { protocol, values: payload.credentials });
      }
      onSaved();
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string; detail?: string })?.error
          ?? (e.body as { detail?: string })?.detail;
        setError(bodyMsg ?? `Speichern fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
      setSaving(false);
    }
  };

  const isLoading =
    connectionSchemaState.status === 'loading'
    || credentialsSchemaState.status === 'loading'
    || existingConnectionState.status === 'loading'
    || existingCredentialsState.status === 'loading';
  const canSave = testStatus.kind === 'passed';

  const testBanner = useMemo(() => {
    switch (testStatus.kind) {
      case 'idle':
        return <span class="muted" style={{ fontSize: 'var(--fs-xs)' }}>
          Erst „Test" klicken, um die Verbindung zu prüfen. Speichern ist bis dahin blockiert.
        </span>;
      case 'running':
        return <span class="muted" style={{ fontSize: 'var(--fs-xs)' }}>Test läuft (max. 15 s)…</span>;
      case 'passed':
        return <span style={{ color: 'var(--status-success)', fontSize: 'var(--fs-xs)' }}>
          ✓ Verbindung OK. Du kannst jetzt speichern.
        </span>;
      case 'failed':
        return <span style={{ color: 'var(--status-error)', fontSize: 'var(--fs-xs)' }}>
          ✗ {testStatus.error}
        </span>;
    }
  }, [testStatus]);

  const renderField = (
    p: CredentialProperty,
    values: ValuesMap,
    setter: (key: string, value: unknown) => void,
    scope: 'conn' | 'cred',
  ) => (
    <div class="hv-modal__field" key={`${scope}-${p.key}`}>
      <label>
        {p.title}
        {p.required && <span style={{ color: 'var(--status-error)' }}> *</span>}
      </label>
      {isUrlField(p) ? (
        <UrlComposer
          segments={urlSegments[p.key] ?? { scheme: 'https', host: hostHint, port: '', manualOverride: false, manualValue: '' }}
          onChange={(patch) => setUrlSegment(p.key, patch)}
          disabled={saving}
        />
      ) : isEnumField(p) ? (
        <>
          <select
            value={values[p.key] === null || values[p.key] === undefined ? '' : String(values[p.key])}
            onChange={(e) => setter(p.key, (e.currentTarget as HTMLSelectElement).value)}
            disabled={saving}
          >
            {!p.required && <option value="">(keine Auswahl)</option>}
            {(p.enumMembers ?? []).map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          {p.helpText && <span class="muted" style={{ fontSize: 'var(--fs-xs)' }}>{p.helpText}</span>}
        </>
      ) : isPortField(p) ? (
        <>
          <input
            type="number"
            min={1}
            max={65535}
            value={values[p.key] === null || values[p.key] === undefined ? '' : String(values[p.key])}
            placeholder={p.placeholder ?? ''}
            onInput={(e) => setter(p.key, (e.currentTarget as HTMLInputElement).value)}
            disabled={saving}
            style={{ maxWidth: '8rem' }}
          />
          {p.helpText && <span class="muted" style={{ fontSize: 'var(--fs-xs)' }}>{p.helpText}</span>}
        </>
      ) : p.type === 'boolean' ? (
        <label class="row-2" style={{ fontSize: 'var(--fs-sm)' }}>
          <input
            type="checkbox"
            checked={Boolean(values[p.key])}
            onChange={(e) => setter(p.key, (e.target as HTMLInputElement).checked)}
            disabled={saving}
          />
          <span class="muted">{p.helpText ?? ''}</span>
        </label>
      ) : (
        <>
          <input
            type={p.type === 'secret' ? 'password' : (p.type === 'integer' ? 'number' : 'text')}
            value={values[p.key] === null || values[p.key] === undefined ? '' : String(values[p.key])}
            placeholder={p.placeholder ?? ''}
            onFocus={(e) => {
              const key = `${scope}:${p.key}`;
              if (p.type === 'secret' && !touched.has(key)
                  && (e.currentTarget as HTMLInputElement).value === SECRET_MASK) {
                setter(p.key, '');
              }
            }}
            onInput={(e) => setter(p.key, (e.currentTarget as HTMLInputElement).value)}
            disabled={saving}
          />
          {p.helpText && <span class="muted" style={{ fontSize: 'var(--fs-xs)' }}>{p.helpText}</span>}
        </>
      )}
    </div>
  );

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !saving && onClose()}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">
            Anschluss{hasCredentials ? ' & Zugangsdaten' : ''} für <span class="mono">{deviceDisplayName}</span>
          </h2>
          <button type="button" class="hv-modal__close" onClick={onClose}
                  aria-label="Schließen" disabled={saving}>×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__hero">
            <HeroCredentials size={72} style={{ color: 'var(--accent)' }} />
          </div>
          <p class="muted" style={{ marginTop: 0 }}>
            Protokoll: <code class="mono">{protocol}</code>.
            {hasCredentials
              ? <> Zugangsdaten werden AES-256-GCM verschlüsselt gespeichert. Passwörter erscheinen beim erneuten Öffnen als <code class="mono">{SECRET_MASK}</code> — nur ändern wenn du sie neu eingibst.</>
              : <> Dieses Protokoll hat keine Authentifizierung — nur Verbindungsparameter erforderlich.</>}
          </p>
          {isLoading && <p class="muted">Lade Schema…</p>}
          {connectionSchemaState.status === 'error' && (
            <p class="hv-modal__error">Verbindungs-Schema konnte nicht geladen werden.</p>
          )}
          {connectionSchemaState.status === 'ok' && initialised && (
            <section>
              <h3 style={{ fontSize: 'var(--fs-sm)', margin: '0 0 0.75rem 0', color: 'var(--text-muted)' }}>
                Verbindung
              </h3>
              {connectionSchemaState.data.properties.map((p) =>
                renderField(p, connectionValues, setConnField, 'conn'))}
            </section>
          )}
          {hasCredentials && initialised && credentialsSchemaState.status === 'ok' && credentialsSchemaState.data && (
            <section style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: 'var(--fs-sm)', margin: '0 0 0.75rem 0', color: 'var(--text-muted)' }}>
                Zugangsdaten
              </h3>
              {credentialsSchemaState.data.properties.map((p) =>
                renderField(p, credentialValues, setCredField, 'cred'))}
            </section>
          )}
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ flex: '1 1 100%', minWidth: 0 }}>{testBanner}</div>
          <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button type="button" class="hv-btn"
                  onClick={runTest}
                  disabled={saving || !initialised || testStatus.kind === 'running'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            {testStatus.kind === 'running' && (
              <CredentialsTest size={18} style={{ color: 'var(--accent)' }} />
            )}
            {testStatus.kind === 'running' ? 'Teste…' : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconTest size={14} />Test</span>
            )}
          </button>
          {plantId && testStatus.kind === 'passed' && (
            <button type="button" class="hv-btn"
                    onClick={() => setShowApplyTemplate(true)}
                    disabled={saving}
                    title="Ein bereits publiziertes Schema-Template auf dieses Device kippen">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconTemplateApply size={14} />Template anwenden…</span>
            </button>
          )}
          <button type="button" class="hv-btn hv-btn--primary"
                  onClick={save}
                  disabled={saving || !initialised || !canSave}
                  title={canSave ? undefined : 'Erst mit „Test" die Verbindung prüfen'}>
            {saving ? 'Speichere…' : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconSave size={14} />Speichern</span>
            )}
          </button>
        </div>
      </div>
      {showApplyTemplate && plantId && (
        <ApplySchemaTemplateModal
          plantId={plantId}
          deviceId={deviceId}
          deviceProtocol={protocol}
          isTargetEmpty={isTargetEmpty ?? false}
          onApplied={(result) => {
            onSchemaApplied?.(result);
          }}
          onClose={() => setShowApplyTemplate(false)}
        />
      )}
    </div>,
    document.body,
  );
}

function UrlComposer({
  segments,
  onChange,
  disabled,
}: {
  segments: UrlSegments;
  onChange(patch: Partial<UrlSegments>): void;
  disabled: boolean;
}) {
  const preview = composeUrl(segments);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      {!segments.manualOverride && (
        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          <select
            value={segments.scheme}
            onChange={(e) => onChange({ scheme: (e.currentTarget as HTMLSelectElement).value as 'http' | 'https' })}
            disabled={disabled}
            style={{ minWidth: '5.5rem' }}
          >
            <option value="https">https</option>
            <option value="http">http</option>
          </select>
          <span class="muted">://</span>
          <input
            type="text"
            value={segments.host}
            placeholder="Host / IP"
            onInput={(e) => onChange({ host: (e.currentTarget as HTMLInputElement).value })}
            disabled={disabled}
            style={{ flex: '1 1 auto', minWidth: 0 }}
          />
          <span class="muted">:</span>
          <input
            type="text"
            value={segments.port}
            placeholder="Port (opt.)"
            onInput={(e) => onChange({ port: (e.currentTarget as HTMLInputElement).value.replace(/[^0-9]/g, '') })}
            disabled={disabled}
            style={{ width: '5.5rem' }}
          />
        </div>
      )}
      {segments.manualOverride && (
        <input
          type="text"
          value={segments.manualValue}
          placeholder="Vollständige URL"
          onInput={(e) => onChange({ manualValue: (e.currentTarget as HTMLInputElement).value })}
          disabled={disabled}
        />
      )}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: 'var(--fs-xs)' }}>
        <span class="muted">Wird gespeichert als:</span>
        <code class="mono" style={{ flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {preview || '(leer)'}
        </code>
        <label class="row-2" style={{ fontSize: 'var(--fs-xs)' }}>
          <input
            type="checkbox"
            checked={segments.manualOverride}
            onChange={(e) => onChange({ manualOverride: (e.currentTarget as HTMLInputElement).checked, manualValue: preview })}
            disabled={disabled}
          />
          <span class="muted">manuell überschreiben</span>
        </label>
      </div>
    </div>
  );
}
